import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { parseAgentModePrefix, type AgentModePrefix } from "@/components/flexkit/chat/agent-mode-prefix";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { IconSearchlinesSparkle } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconSearchlinesSparkle";
import { IconCodeAssistant } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconCodeAssistant";
import { IconFork } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconFork";
import { IconMagnifyingGlass } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconMagnifyingGlass";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import type { AgentMessage_Type, AgentMessage_Role } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCreateAgentExecutionConversationToken, useGetAgentExecution } from "@/queries/agent-queries";
import { resolveAgentDisplayName, getAgentMessageBadgeLabel } from "@/components/flexkit/chat/agent-message-label";
import { ImageLightbox, type LightboxImage } from "@/components/ai/chat/ImageLightbox";
import { ImageThumbnail } from "@/components/ai/chat/ImageThumbnail";

const modeConfig = {
    agent: {
        label: "Agent",
        Icon: IconCodeAssistant,
        colorClass: "text-content-brand",
    },
    spec: {
        label: "Plan",
        Icon: IconSearchlinesSparkle,
        colorClass: "text-content-brand-accent-01",
    },
    ralph: {
        label: "Ralph",
        Icon: IconRalphWiggum,
        colorClass: "text-content-orange",
    },
    review: {
        label: "Review",
        Icon: IconMagnifyingGlass,
        colorClass: "text-content-brand",
    },
};

const AgentModeBadge: FC<{ mode: AgentModePrefix }> = ({ mode }) => {
    const { label, Icon, colorClass } = modeConfig[mode];

    return (
        <div className={cn("flex items-center gap-1 font-normal", colorClass)}>
            <Icon size={14} className="shrink-0" />
            <span className="text-xs">{label}</span>
        </div>
    );
};

const SubagentBadge: FC<{ role: AgentMessage_Role; messageType: AgentMessage_Type; senderExecutionId: string }> = ({
    role,
    messageType,
    senderExecutionId,
}) => {
    const { data: senderExecution } = useGetAgentExecution(senderExecutionId || undefined);
    const displayName = resolveAgentDisplayName(senderExecution?.metadata?.name);
    const label = getAgentMessageBadgeLabel(role, messageType, displayName);

    return (
        <div className="flex items-center gap-1 font-normal text-content-secondary">
            <IconFork size={14} className="shrink-0" />
            <span className="text-xs">{label}</span>
        </div>
    );
};

function useInlineBlobUrl(data: Uint8Array, mimeType: string): string {
    const [imageUrl, setImageUrl] = useState<string>("");

    useEffect(() => {
        if (!data || data.length === 0) {
            return;
        }

        // Handle case where data might be a regular array or object instead of Uint8Array
        let blobData: BlobPart;
        if (data instanceof Uint8Array) {
            blobData = data;
        } else if (Array.isArray(data)) {
            blobData = new Uint8Array(data);
        } else if (typeof data === "object" && data !== null) {
            // Protobuf-es might return an object with numeric keys
            const values = Object.values(data as Record<string, number>);
            blobData = new Uint8Array(values);
        } else {
            return;
        }

        const blob = new Blob([blobData], { type: mimeType || "image/png" });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [data, mimeType]);

    return imageUrl;
}

/**
 * Fetches a blob from a remote URL using the conversation auth token,
 * then creates a local object URL for rendering.
 */
function useRemoteBlobUrl(blobUrl: string | undefined, token: string | undefined): string {
    const [imageUrl, setImageUrl] = useState<string>("");

    useEffect(() => {
        if (!blobUrl || !token) {
            return;
        }

        const controller = new AbortController();

        void (async () => {
            try {
                const resp = await fetch(blobUrl, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) {
                    console.warn(`Failed to fetch blob from ${blobUrl}: ${resp.status}`);
                    return;
                }
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                setImageUrl(url);
            } catch {
                // Aborted or network error — ignore
            }
        })();

        return () => {
            controller.abort();
            setImageUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return "";
            });
        };
    }, [blobUrl, token]);

    return imageUrl;
}

function useBlobUrl(image: ImageFragment, token: string | undefined): string {
    const remoteUrl = useRemoteBlobUrl(image.blobUrl, token);
    const inlineUrl = useInlineBlobUrl(image.data, image.mimeType);
    // Prefer remote URL when available (offloaded blob), fall back to inline data
    return image.blobUrl ? remoteUrl : inlineUrl;
}

type ImageFragment = {
    id: string;
    data: Uint8Array;
    mimeType: string;
    blobUrl?: string;
};

const UserImageGallery: FC<{ images: ImageFragment[]; token?: string }> = ({ images, token }) => {
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [blobUrls, setBlobUrls] = useState<Map<string, string>>(new Map());
    const isLightboxOpen = lightboxIndex >= 0;

    // Track blob URLs as they're created by child components
    const registerUrl = useCallback((id: string, url: string) => {
        setBlobUrls((prev) => {
            if (prev.get(id) === url) return prev;
            const next = new Map(prev);
            next.set(id, url);
            return next;
        });
    }, []);

    const lightboxImages: LightboxImage[] = useMemo(
        () =>
            images
                .map((img) => {
                    const url = blobUrls.get(img.id);
                    // TODO: the image fragment type doesn't carry a filename — once it does,
                    // use it here so the lightbox header shows a meaningful name.
                    return url ? { src: url, alt: `User uploaded image` } : null;
                })
                .filter((img): img is LightboxImage => img !== null),
        [images, blobUrls],
    );

    const handleOpenLightbox = useCallback((index: number) => {
        setLightboxIndex(index);
    }, []);

    const handleCloseLightbox = useCallback((open: boolean) => {
        if (!open) setLightboxIndex(-1);
    }, []);

    if (images.length === 0) return null;

    const gridClass =
        images.length === 1 ? "grid-cols-1" : images.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3";

    return (
        <>
            <div className={cn("grid gap-2", gridClass)}>
                {images.map((img, index) => (
                    // eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- tracking ID is on the inner button
                    <UserImageThumbnailWithRegistration
                        key={img.id}
                        image={img}
                        index={index}
                        onClick={handleOpenLightbox}
                        onUrlReady={registerUrl}
                        token={token}
                    />
                ))}
            </div>
            {lightboxImages.length > 0 && (
                <ImageLightbox
                    images={lightboxImages}
                    currentIndex={isLightboxOpen ? lightboxIndex : 0}
                    onIndexChange={setLightboxIndex}
                    open={isLightboxOpen}
                    onOpenChange={handleCloseLightbox}
                />
            )}
        </>
    );
};

const UserImageThumbnailWithRegistration: FC<{
    image: ImageFragment;
    index: number;
    onClick: (index: number) => void;
    onUrlReady: (id: string, url: string) => void;
    token?: string;
}> = ({ image, index, onClick, onUrlReady, token }) => {
    const url = useBlobUrl(image, token);

    useEffect(() => {
        if (url) {
            onUrlReady(image.id, url);
        }
    }, [url, image.id, onUrlReady]);

    if (!url) return null;
    return (
        // eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- tracking ID is on the inner button
        <ImageThumbnail
            src={url}
            alt={`User uploaded image ${index + 1}`}
            onClick={() => onClick(index)}
            className="h-24 w-full"
        />
    );
};

/**
 * Process a text fragment to strip agent mode prefix.
 */
function processTextFragment(content: string): { displayContent: string } {
    const { strippedContent } = parseAgentModePrefix(content);
    return { displayContent: strippedContent };
}

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
    agentExecutionId?: string;
}> = ({ message, isPending, agentExecutionId }) => {
    const { data: conversationToken } = useCreateAgentExecutionConversationToken(agentExecutionId);

    // Check if any fragment is an agentMessage (from a subagent via AgentMessage proto)
    const agentMsgFragment = message.fragments.find((f) => f.type === "agentMessage");
    const isFromSubagent = !!agentMsgFragment;

    // Check if any fragment has an agent mode prefix
    const agentMode = message.fragments.reduce<AgentModePrefix>((found, fragment) => {
        if (found !== "agent") return found;
        if (fragment.type === "text") {
            return parseAgentModePrefix(fragment.content).mode;
        }
        return "agent";
    }, "agent");

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full flex-col items-end gap-1 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            {isFromSubagent && agentMsgFragment.type === "agentMessage" ? (
                <SubagentBadge
                    role={agentMsgFragment.role}
                    messageType={agentMsgFragment.messageType}
                    senderExecutionId={agentMsgFragment.senderExecutionId}
                />
            ) : (
                <AgentModeBadge mode={agentMode} />
            )}
            <div
                className={cn(
                    "min-w-0 max-w-full flex-1 rounded-xl border border-border-subtle bg-surface-muted px-3 py-2 text-base text-content-primary shadow-sm transition-opacity",
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {message.fragments.map((fragment) => {
                            if (fragment.type === "text") {
                                const { displayContent } = processTextFragment(fragment.content);
                                return <Markdown content={displayContent} key={fragment.id} />;
                            }
                            if (fragment.type === "agentMessage") {
                                return <Markdown content={fragment.payload} key={fragment.id} />;
                            }
                            return null;
                        })}
                        <UserImageGallery
                            images={message.fragments.filter(
                                (f): f is ImageFragment & { type: "image" } => f.type === "image",
                            )}
                            token={conversationToken}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
